import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/App";
import { useVideoUrl } from "@/hooks/useVideoUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ArrowRight, Play, Pause, Loader2, Monitor } from "lucide-react";
import { toast } from "sonner";
import TimelineTrack from "@/components/timeline/TimelineTrack";
import { AspectRatioSidebar } from "@/components/restripe/AspectRatioSidebar";
import { CroppedVideoPreview } from "@/components/restripe/CroppedVideoPreview";
import { RestripeToolPanel } from "@/components/restripe/RestripeToolPanel";

const sortByTime = (arr) => [...arr].sort((a, b) => a.start_time - b.start_time);

const interpolateKeyframes = (keyframes, time) => {
  if (!keyframes || keyframes.length === 0) return null;
  if (keyframes.length === 1) return { x: keyframes[0].x, y: keyframes[0].y };
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);
  if (time <= sorted[0].time) return { x: sorted[0].x, y: sorted[0].y };
  if (time >= sorted[sorted.length - 1].time)
    return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      const t = (time - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
      return {
        x: sorted[i].x + (sorted[i + 1].x - sorted[i].x) * t,
        y: sorted[i].y + (sorted[i + 1].y - sorted[i].y) * t,
      };
    }
  }
  return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
};

const RestripePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const audioInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);

  const [selectedAspectRatio, setSelectedAspectRatio] = useState(null);
  const [aspectRatios, setAspectRatios] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [cropPositions, setCropPositions] = useState({});
  const [graphics, setGraphics] = useState([]);
  const [selectedGraphic, setSelectedGraphic] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30);
  const [playing, setPlaying] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [dragging, setDragging] = useState(null);
  const [newGraphic, setNewGraphic] = useState({
    type: "super", content: "", start_time: 0, end_time: 5,
    position: { x: 50, y: 90 }, style: {},
  });

  // ── Data Loading ──
  const fetchData = useCallback(async () => {
    try {
      const [projectRes, scenesRes, cropRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/scenes`),
        axios.get(`${API}/projects/${projectId}/crop-positions`),
      ]);
      const proj = projectRes.data;
      setProject(proj);
      if (proj.video_duration) setDuration(proj.video_duration);
      const ars = proj.aspect_ratios || [];
      setAspectRatios(ars);

      const posMap = {};
      (scenesRes.data || []).forEach((s) => { posMap[s.id] = {}; });
      (cropRes.data || []).forEach((cp) => {
        if (!posMap[cp.scene_id]) posMap[cp.scene_id] = {};
        posMap[cp.scene_id][cp.aspect_ratio] = {
          x: cp.center_x ?? cp.x ?? 50, y: cp.center_y ?? cp.y ?? 50,
          keyframes: cp.keyframes || [],
        };
      });
      setCropPositions(posMap);
      setScenes(sortByTime(scenesRes.data || []));
      if (ars.length > 0 && !selectedAspectRatio) setSelectedAspectRatio(ars[0]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGraphics = useCallback(async () => {
    if (!selectedAspectRatio) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/graphics`, { params: { aspect_ratio: selectedAspectRatio } });
      setGraphics(res.data || []);
    } catch (err) { console.warn("Failed to fetch graphics:", err); }
  }, [projectId, selectedAspectRatio]);

  useEffect(() => { fetchGraphics(); }, [fetchGraphics]);

  useEffect(() => {
    if (!videoRef.current) return;
    const handler = () => setCurrentTime(videoRef.current?.currentTime || 0);
    const el = videoRef.current;
    el.addEventListener("timeupdate", handler);
    return () => el?.removeEventListener("timeupdate", handler);
  }, [project, selectedAspectRatio]);

  useEffect(() => {
    if (scenes.length === 0) return;
    const idx = scenes.findIndex((s) => currentTime >= s.start_time && currentTime < s.end_time);
    if (idx >= 0 && idx !== currentScene) setCurrentScene(idx);
  }, [currentTime, scenes]);

  // ── Aspect Ratio Switching ──
  const switchAspectRatio = useCallback((ar) => {
    if (ar === selectedAspectRatio) return;
    setSelectedAspectRatio(ar);
    setSelectedGraphic(null);
  }, [selectedAspectRatio]);

  // ── Copy Overlays ──
  const copyOverlaysFrom = useCallback(async (sourceAr, targetAr) => {
    setCopyLoading(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/graphics/copy-from`, {
        source_aspect_ratio: sourceAr,
        target_aspect_ratio: targetAr,
      });
      toast.success(`Copied ${res.data.copied} overlays from ${sourceAr} to ${targetAr}`);
      fetchGraphics();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCopyLoading(false);
    }
  }, [projectId, fetchGraphics]);

  // ── Graphics CRUD ──
  const addGraphic = async () => {
    if (!selectedAspectRatio) { toast.error("Select an aspect ratio first"); return; }
    try {
      const res = await axios.post(`${API}/projects/${projectId}/graphics`, {
        project_id: projectId, ...newGraphic, aspect_ratio: selectedAspectRatio,
      });
      const doc = res.data;
      if (doc._id) delete doc._id;
      setGraphics((prev) => [...prev, doc]);
      setDialogOpen(false);
      setNewGraphic({ type: "super", content: "", start_time: currentTime, end_time: Math.min(currentTime + 5, duration), position: { x: 50, y: 90 }, style: {} });
      toast.success("Graphic added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteGraphic = async (id) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/graphics/${id}`);
      setGraphics((prev) => prev.filter((g) => g.id !== id));
      if (selectedGraphic?.id === id) setSelectedGraphic(null);
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateGraphicPosition = async (graphic, newPos) => {
    const updated = { ...graphic, position: newPos };
    setGraphics((prev) => prev.map((g) => (g.id === graphic.id ? updated : g)));
    setSelectedGraphic(updated);
    try {
      await axios.put(`${API}/projects/${projectId}/graphics/${graphic.id}`, { position: newPos });
    } catch (err) { console.warn("Position save failed:", err); }
  };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/projects/${projectId}/audio/upload`, formData);
      toast.success("Audio uploaded - will replace original audio on export");
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  // ── Playback ──
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause(); else videoRef.current.play();
    setPlaying(!playing);
  };
  const seekTo = (time) => { if (videoRef.current) videoRef.current.currentTime = time; setCurrentTime(time); };
  const seekToScene = (idx) => { if (scenes[idx]) { setCurrentScene(idx); seekTo(scenes[idx].start_time); } };

  // ── Crop viewport ──
  const getCropPosition = useCallback(() => {
    if (!selectedAspectRatio || scenes.length === 0) return { x: 50, y: 50 };
    const scene = scenes[currentScene];
    if (!scene) return { x: 50, y: 50 };
    const cropData = cropPositions[scene.id]?.[selectedAspectRatio];
    if (!cropData) return { x: 50, y: 50 };
    if (cropData.keyframes?.length >= 2) {
      const interp = interpolateKeyframes(cropData.keyframes, currentTime);
      if (interp) return interp;
    }
    return { x: cropData.x, y: cropData.y };
  }, [selectedAspectRatio, scenes, currentScene, cropPositions, currentTime]);

  const cropPos = getCropPosition();

  // ── Drag logic ──
  const handleDragStart = (e, graphic) => {
    e.preventDefault(); e.stopPropagation();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({ id: graphic.id, startMouseX: e.clientX, startMouseY: e.clientY, startPosX: graphic.position?.x ?? 50, startPosY: graphic.position?.y ?? 50, canvasW: rect.width, canvasH: rect.height });
    setSelectedGraphic(graphic);
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragging.startMouseX;
    const dy = e.clientY - dragging.startMouseY;
    const newX = Math.max(0, Math.min(100, dragging.startPosX + (dx / dragging.canvasW) * 100));
    const newY = Math.max(0, Math.min(100, dragging.startPosY + (dy / dragging.canvasH) * 100));
    setGraphics((prev) => prev.map((g) => g.id === dragging.id ? { ...g, position: { ...g.position, x: Math.round(newX), y: Math.round(newY) } } : g));
    setSelectedGraphic((prev) => prev?.id === dragging.id ? { ...prev, position: { x: Math.round(newX), y: Math.round(newY) } } : prev);
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    if (!dragging) return;
    const graphic = graphics.find((g) => g.id === dragging.id);
    if (graphic) updateGraphicPosition(graphic, graphic.position);
    setDragging(null);
  }, [dragging, graphics]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => { window.removeEventListener("mousemove", handleMouseMove); window.removeEventListener("mouseup", handleMouseUp); };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const { videoUrl: videoStreamUrl } = useVideoUrl(projectId, "source", !!project?.source_video);
  const visibleGraphics = graphics.filter((g) => currentTime >= g.start_time && currentTime <= g.end_time);

  const sceneKeyframes = useMemo(() => {
    if (!selectedAspectRatio) return {};
    const map = {};
    scenes.forEach((s) => { const kfs = cropPositions[s.id]?.[selectedAspectRatio]?.keyframes; if (kfs?.length) map[s.id] = kfs; });
    return map;
  }, [scenes, cropPositions, selectedAspectRatio]);

  const graphicCounts = useMemo(() => {
    const counts = {};
    if (selectedAspectRatio) counts[selectedAspectRatio] = graphics.length;
    return counts;
  }, [selectedAspectRatio, graphics]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
    </div>
  );

  if (aspectRatios.length === 0) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="restripe-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <div className="text-center space-y-4">
        <Monitor className="w-10 h-10 text-cyan-400/30 mx-auto" />
        <h2 className="font-['Rajdhani'] text-xl tracking-wide">NO ASPECT RATIOS CONFIGURED</h2>
        <p className="text-sm text-cyan-200/40 max-w-md">Set up alternative aspect ratios in the Crop Workspace first, then return here to add graphics and overlays per crop.</p>
        <Button onClick={() => navigate(`/crop-workspace?project=${projectId}`)} className="h-10 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" data-testid="go-to-crop-btn">GO TO CROP WORKSPACE</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="restripe-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate(`/project/${projectId}`)} className="text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="font-['Rajdhani'] text-lg tracking-wide">RESTRIPE</h1>
              <p className="text-sm text-cyan-200/30">{project?.name} — {selectedAspectRatio || "Select ratio"} — {graphics.length} overlay{graphics.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/project/${projectId}/encoding`)} className="h-10 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" data-testid="continue-btn">
            CONTINUE <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8">
        <div className="grid lg:grid-cols-12 gap-6">
          <AspectRatioSidebar
            aspectRatios={aspectRatios}
            selectedAspectRatio={selectedAspectRatio}
            onSelect={switchAspectRatio}
            graphicCounts={graphicCounts}
            onCopyFrom={copyOverlaysFrom}
            copyLoading={copyLoading}
          />

          <div className="lg:col-span-7 space-y-4">
            <CroppedVideoPreview
              canvasRef={canvasRef}
              videoRef={videoRef}
              videoStreamUrl={videoStreamUrl}
              selectedAspectRatio={selectedAspectRatio}
              cropPos={cropPos}
              currentScene={currentScene}
              sceneCount={scenes.length}
              visibleGraphics={visibleGraphics}
              selectedGraphic={selectedGraphic}
              dragging={dragging}
              playing={playing}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onDragStart={handleDragStart}
              onSelectGraphic={setSelectedGraphic}
              onDurationLoaded={setDuration}
            />

            <div className="flex items-center gap-4">
              <button onClick={togglePlay} className="p-2 text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="play-btn">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <div className="flex-1">
                <Slider value={[currentTime]} onValueChange={([v]) => seekTo(v)} max={duration} step={0.1} data-testid="timeline-slider" />
              </div>
              <span className="text-sm text-cyan-400/60 font-mono w-24 text-right">{currentTime.toFixed(1)}s / {duration.toFixed(1)}s</span>
            </div>

            {scenes.length > 0 && (
              <TimelineTrack
                scenes={scenes} duration={duration} currentTime={currentTime} currentScene={currentScene}
                playing={playing} onTogglePlay={togglePlay} onSeek={seekTo} onSceneSelect={seekToScene}
                onSplit={() => {}} onDelete={() => {}} onTrim={() => {}} onAddScene={() => {}}
                sceneKeyframes={sceneKeyframes}
                overlays={graphics} selectedOverlay={selectedGraphic?.id || null}
                onOverlaySelect={(id) => setSelectedGraphic(graphics.find((g) => g.id === id) || null)}
                onOverlayMove={(id, s, e, c) => { setGraphics((p) => p.map((g) => g.id === id ? { ...g, start_time: s, end_time: e } : g)); if (c) axios.put(`${API}/projects/${projectId}/graphics/${id}`, { start_time: s, end_time: e }).catch(() => {}); }}
                onOverlayTrim={(id, f, v, c) => { setGraphics((p) => p.map((g) => g.id === id ? { ...g, [f]: v } : g)); if (c) axios.put(`${API}/projects/${projectId}/graphics/${id}`, { [f]: v }).catch(() => {}); }}
                onOverlayDelete={deleteGraphic}
                onAddOverlay={(type) => { setNewGraphic((p) => ({ ...p, type, start_time: currentTime, end_time: Math.min(currentTime + 3, duration) })); setDialogOpen(true); }}
              />
            )}
          </div>

          <RestripeToolPanel
            selectedAspectRatio={selectedAspectRatio}
            graphics={graphics}
            selectedGraphic={selectedGraphic}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            newGraphic={newGraphic}
            setNewGraphic={setNewGraphic}
            duration={duration}
            uploading={uploading}
            audioInputRef={audioInputRef}
            hasAudioTracks={project?.audio_tracks?.length > 0}
            onAddGraphic={addGraphic}
            onDeleteGraphic={deleteGraphic}
            onSelectGraphic={setSelectedGraphic}
            onSeekTo={seekTo}
            onUpdatePosition={updateGraphicPosition}
            onUploadAudio={uploadAudio}
          />
        </div>
      </main>
    </div>
  );
};

export default RestripePage;
